/* eslint-disable no-undef */
/* eslint-disable react/no-unused-class-component-methods */
/* eslint-disable react/destructuring-assignment */

import PropTypes from 'prop-types';
import { Component } from 'react';
import styled, { css } from 'styled-components';
import { v4 as uuidV4 } from 'uuid';

import { Box } from '@hivebrite/design-system/Box';
import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import { Label } from '@hivebrite/design-system/Label';
import { responsive } from '@hivebrite/design-system/Theme';

export const createHeaderTemplate =
  () => `<div class="search-view-dropdown__section-title">
            ${I18n.t('web.search.people')}
          </div>`;

export const createSuggestionTemplate = (data, disable, type) => {
  const fullName = data.full_name || `${data.firstname} ${data.lastname}`;
  const isDisabled =
    disable.attribute && data[disable.attribute] ? 'disable' : '';
  const shouldHaveLink = data.activeLink || type === 'header';
  const headline =
    data.headline && data.headline.length > 80
      ? `${data.headline.substr(0, 80)}...`
      : data.headline;

  const contentHtml = `
    <div class="grid grid--full" role="presentation">
      <div class="inline-block v-middle relative">
        ${
          data.deceased
            ? `
          <div class="deceased-wrapper deceased-wrapper--min"
              title="${I18n.t('views.backoffice.deceased')}"
              data-toggle="tooltip">
            <div class="corner"></div>
          </div>
        `
            : ''
        }
        <img class="user-item__img--rounded user-item__img--40"
            src="${data.photo_url || data.photo_thumb_url}"
            alt="${__('User profile picture')}" />
      </div>
      <div class="grid__item ten-twelfths soft-half--left v-middle">
        <div class="weight--bold">${fullName}</div>
        ${headline ? `<div class="color-gray">${headline}</div>` : ''}
        ${
          disable.attribute && data.confirmed
            ? `
          <div class="absolute east push--right weight--bold disable-label">
            ${disable.label}
          </div>
        `
            : ''
        }
      </div>
    </div>`;

  const commonAttributes = `
      class="muted-link search-item relative ${isDisabled}"
      aria-label="${fullName}"
      aria-selected="false"
      role="option"
      style="cursor: pointer"`;

  if (shouldHaveLink) {
    const userPath = Routes.user_path(data.id);
    return `<a href="${userPath}" ${commonAttributes}>${contentHtml}</a>`;
  }

  return `<div ${commonAttributes}>${contentHtml}</div>`;
};

export const createFooterTemplate = (context, uuid, footer, theme) => {
  if (context.suggestions.length >= 7) {
    return `
      <footer class="text--center">
        <a href="#"
          data-default-href="${Routes.people_search_path({ keywords: context.query })}"
          class="js-typeahead-see-more-${uuid} weight--bold soft-half block">
          ${I18n.t('web.shared.see_more')}
        </a>
      </footer>
    `;
  }

  if (footer.label && footer.onClick) {
    return `
      <footer class="js-typeahead-footer-${uuid} typeahead-footer text--center weight--bold soft-half block">
        <div style="text-align: center;">
          <span style="display: inline-block; cursor: pointer; color: ${theme?.colors?.primary || '#4733ff'};">
            ${footer.label}
          </span>
        </div>
      </footer>
    `;
  }

  return null;
};

class UserSearch extends Component {
  componentDidMount() {
    const {
      disable,
      focusOnRender,
      footer,
      headerActive,
      labelDisplayWhenSelected,
      limitUsersDisplay,
      networkId,
      theme,
      type,
      topicId,
      excludedUserIds,
    } = this.props;
    const uuid = uuidV4();
    let url;

    // @TODO We need to uniformise url parameters and server responses before removing this hack
    switch (type) {
      case 'targeting':
        url = Routes.targeting_users_path({
          query: { who: '%QUERY' },
          sort_by: 'lastname',
          search_type: type,
        });
        break;
      case 'addAttendee':
      case 'experts':
        url = Routes.quick_search_backoffice_networks_path({
          term: '%QUERY',
          network_id: networkId,
          sort_by: 'lastname',
          search_type: type,
        });
        break;
      case 'user_invitations':
        url = Routes.user_search_user_user_invitations_path({
          user_id: AlumniConnect.currentUser?.id,
          query: { who: '%QUERY' },
          sort_by: 'lastname',
          search_type: type,
        });
        break;
      case 'topic_admin':
        url = Routes.quick_search_backoffice_networks_path({
          term: '%QUERY',
          sort_by: 'lastname',
          network_id: networkId,
          topic_id: topicId,
          excluded_user_ids: excludedUserIds,
          search_type: type,
        });
        break;
      default:
        url = Routes.quick_search_users_path({
          term: '%QUERY',
          sort_by: 'lastname',
          format: 'js',
          search_type: type,
        });
    }

    const engine = new Bloodhound({
      queryTokenizer: Bloodhound.tokenizers.whitespace,
      datumTokenizer: Bloodhound.tokenizers.whitespace,
      remote: {
        url: unescape(url),
        wildcard: '%QUERY',
        transform: (response) => {
          const data = response.users || response;

          this.props.onFetchResults?.(data);

          return data;
        },
      },
    });

    engine.initialize();

    $(this.searchInput).typeahead(
      {
        highlight: true,
        minLength: 1,
        autoselect: true,
        classNames: {
          menu: `dropdown-menu hard search-view-dropdown--${type}`,
          dataset: `relative search-view-dropdown__dataset ${
            headerActive
              ? 'search-view-dropdown__dataset--with-left-header'
              : ''
          }`,
          cursor: 'search-view--selected',
        },
      },
      {
        display: labelDisplayWhenSelected,
        limit: limitUsersDisplay,
        templates: {
          header: () => (headerActive ? createHeaderTemplate() : null),
          suggestion: (data) => createSuggestionTemplate(data, disable, type),
          footer: (context) =>
            createFooterTemplate(context, uuid, footer, theme),
        },
        source: engine.ttAdapter(),
      },
    );

    $(this.searchInput).on('typeahead:selected', (event, user) => {
      this.props.onSelectUser(user);
    });

    $(document).on('typeahead:beforeselect', (event, user) => {
      if (disable.attribute && user[disable.attribute]) {
        event.preventDefault();
      }
    });

    $(document).on('click', `.js-typeahead-see-more-${uuid}`, (event) => {
      event.preventDefault();

      if (this.props.onSeeAll) {
        this.props.onSeeAll();
      } else {
        // eslint-disable-next-line jquery/no-data
        const path = $(event.currentTarget).data('defaultHref');

        window.location.href = path;
      }
      $(this.searchInput).typeahead('close');
    });

    $(document).on('click', `.js-typeahead-footer-${uuid}`, (event) => {
      event.preventDefault();

      footer.onClick();
      $(this.searchInput).typeahead('close');
    });

    if (focusOnRender) {
      this.searchInput.focus();
    }

    // workaround as typeahead.js doesn't allow us to interact with the
    // elements attributes easily, and the implemention below doesn't require these attributes
    if (this.searchInput) {
      this.searchInput.removeAttribute('aria-readonly');
      this.searchInput.setAttribute('aria-expanded', false);
    }
  }

  render() {
    const {
      defaultValue,
      inputHtml,
      label,
      theme,
      wrapperHtml,
      nameHiddenInput,
      userIdHiddenInput,
    } = this.props;
    // Coming from SCSS for now (input-with-icon) - to remove once moving to DS select/inputs
    const iconPaddingValue = 40;

    return (
      <div className="relative input-with-icon--left" data-testid="user-search">
        {label && <Label id="search-input">{label}</Label>}
        <div {...wrapperHtml} ref={(ref) => (this.searchInputContainer = ref)}>
          <IconWrapper $iconPaddingValue={iconPaddingValue}>
            <FontAwesomeIcon height="16px" iconName="magnifying-glass" />
          </IconWrapper>

          {nameHiddenInput && (
            <input
              type="hidden"
              name={nameHiddenInput}
              value={userIdHiddenInput}
            />
          )}

          <StyledInput
            {...inputHtml}
            ref={(ref) => (this.searchInput = ref)}
            type="search"
            name={inputHtml.name || 'query[who]'}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
            placeholder={
              inputHtml.placeholder || I18n.t('layouts.search_for_a_name')
            }
            autoComplete="off"
            defaultValue={defaultValue}
            className="uk-input"
            id="search-input"
            aria-label={
              inputHtml.placeholder || I18n.t('layouts.search_for_a_name')
            }
            // eslint-disable-next-line react/no-unknown-property
            css={css`
              &:focus {
                border: ${theme?.colors?.primary
                  ? `1px solid ${theme.colors.primary}!important`
                  : ''};
              }
            `}
          />
        </div>
      </div>
    );
  }
}

UserSearch.propTypes = {
  disable: PropTypes.shape({
    attribute: PropTypes.string,
    label: PropTypes.string,
  }),
  focusOnRender: PropTypes.bool,
  footer: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
  }),
  headerActive: PropTypes.bool,
  inputHtml: PropTypes.shape({
    name: PropTypes.string,
    placeholder: PropTypes.string,
    className: PropTypes.string,
  }),
  label: PropTypes.string,
  labelDisplayWhenSelected: PropTypes.string,
  limitUsersDisplay: PropTypes.number,
  networkId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBlur: PropTypes.func,
  onFetchResults: PropTypes.func,
  onFocus: PropTypes.func,
  onSeeAll: PropTypes.func,
  onSelectUser: PropTypes.func.isRequired,
  type: PropTypes.string,
  wrapperHtml: PropTypes.shape({
    className: PropTypes.string,
  }),
  topicId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  excludedUserIds: PropTypes.arrayOf(PropTypes.number),
};

UserSearch.defaultProps = {
  wrapperHtml: {},
  inputHtml: {},
  type: 'default',
  headerActive: false,
  limitUsersDisplay: 7,
  labelDisplayWhenSelected: 'name',
  disable: { attribute: '' },
  focusOnRender: false,
  footer: {},
};

export default UserSearch;

const IconWrapper = styled(Box)`
  ${({ theme, $iconPaddingValue }) => css`
    color: ${theme.input.iconColor};
    height: ${theme.input.iconSize}px;
    position: absolute;
    width: ${theme.input.iconSize}px;
    z-index: 2;
    align-items: center;
    justify-content: center;
    top: 50%;
    left: ${$iconPaddingValue / 2}px;
    transform: translate(-50%, -50%);
  `}
`;

const StyledInput = styled.input`
  ${({ theme }) => css`
    // To avoid zoom on iOS mobile devices, inputs must have font-size set to 16px on s & m
    font-size: ${theme.fontSizes[3]} !important;
    ${responsive('l')`
      font-size: ${theme.fontSizes[2]} !important;
    `}

    height: 40px !important;
    background: ${theme.input.bg} !important;
    color: ${theme.input.color} !important;
    border-color: ${theme.input.borderColor} !important;

    ::placeholder {
      color: ${theme.input.placeholder} !important;
    }
  `}
`;
